import { auth, stroot, subscription as subscriptionAPI } from '@birdi/js-sdk';
import React, { useEffect, useState } from 'react';
import { css, Theme } from '@emotion/react';
import {
  BirdiTheme,
  breakpoints,
  buttonBlue,
  buttonOutlineBlack,
} from '@birdi/theme';
import BackSVG from '@birdi/icons/arrow-left.svg';
import { Link } from 'react-router';
import { FreePlanOption } from './plan-cards/free-plan-option';
import { GrowthPlanOption } from './plan-cards/growth-plan-option';
import { PlusPlanOption } from './plan-cards/plus-plan-option';
import { UltimateOption } from './plan-cards/ultimate-plan-option';
import { useMetaSelector } from '../../../meta/reducer';
import { PaymentIntervalOption, PlanData } from './plan-cards/plan-option';
import { LoadingSpinner } from '@birdi/loading';
import { Modal } from '@birdi/components-common';
import { organisationUsers as organisationUsersAPI } from '@birdi/js-sdk';
import { OrganisationUser } from '@birdi/js-sdk/src/public/organisation-users';
import { UserWorkspaces } from '../../../user-workspaces/user-workspaces';

export const PlanOptions = (): React.ReactElement => {
  const meta = useMetaSelector();
  const organisationId = meta.organisation?.id;
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [paymentInterval, setPaymentInterval] =
    useState<PaymentIntervalOption>('annual');
  const [isLoading, setIsLoading] = useState(true);

  // Check if user lacks organisation:edit permission
  const noEditPermission = !meta.permissions.includes('organisation:edit');
  const [owners, setOwners] = useState<OrganisationUser[]>([]);

  // Check if user has a plan
  const hasPlan = meta.organisation?.planV2 !== null;

  useEffect(() => {
    // get plans based on user's country currency
    subscriptionAPI
      .getDefaultPlanOptions(stroot('otii6o'))
      .then((res) => {
        // Make sure we're extracting the plans array correctly from the response
        const plansData = Array.isArray(res.body)
          ? res.body
          : res.body?.plans && Array.isArray(res.body.plans)
            ? res.body.plans
            : [];
        setPlans(plansData);
        console.log('get plans ', res.body);
        setIsLoading(false);
      })
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    if (noEditPermission && organisationId) {
      const fetchOwners = async () => {
        const owners = await organisationUsersAPI.getOwners(
          stroot('dfj34s'),
          organisationId,
        );
        setOwners(owners.body);
      };
      fetchOwners();
    } else {
      setOwners([]);
    }
  }, [noEditPermission, organisationId]);

  const signOut = async () => {
    sessionStorage.clear();
    localStorage.removeItem('selectedPlanType');
    localStorage.removeItem('cancelPlan');
    localStorage.removeItem('localOldPlan');
    // Pass false to prevent auto-reload, we redirect ourselves below
    await auth.clearCookie(stroot('ph9tza'), false);
    // Replace history and force reload to clear bfcache, so the back button
    // can't show authenticated pages
    window.location.replace('/sign-in');
  };

  if (noEditPermission) {
    return (
      <Modal.Root open>
        <Modal.Header closeable={false} divider={false}>
          Migrating to a new plan
        </Modal.Header>
        <Modal.Body>
          <p>
            Birdi has recently rolled out new plans and your organisation is yet
            to select a new plan. Please contact an organisation admin to advise
            them to log in and select a new plan.
          </p>
          {owners?.length > 0 && (
            <div style={{ margin: '1em 0' }}>
              <strong>Organisation Admins:</strong>
              <ul>
                {owners.map((owner) => (
                  <li key={owner.id}>
                    {owner.fname} {owner.lname} (
                    <a href={`mailto:${owner.email}`}>{owner.email}</a>)
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p>
            Please <a href="mailto:support@birdi.io">contact us</a> for any
            further assistance required.
          </p>
          {/* This modal's overlay covers the header, so the only ways out of a
              plan-less organisation have to live inside it. */}
          <div
            css={css`
              display: flex;
              flex-wrap: wrap;
              gap: 0.5em;
              margin-top: 1em;
            `}
          >
            <UserWorkspaces
              trigger={
                <button
                  type="button"
                  data-testid="no-plan-switch-workspace"
                  css={(theme: BirdiTheme) =>
                    buttonBlue(theme, { baseSize: 'sm' })
                  }
                >
                  Switch workspace
                </button>
              }
            />
            <button
              type="button"
              data-testid="no-plan-log-out"
              onClick={signOut}
              css={(theme: BirdiTheme) =>
                buttonOutlineBlack(theme, { baseSize: 'sm' })
              }
            >
              Log out
            </button>
          </div>
        </Modal.Body>
      </Modal.Root>
    );
  }

  return (
    <div
      css={(theme: Theme) => css`
        background-image: url('https://birdi-public.s3-ap-southeast-2.amazonaws.com/signup-flow/circle-background.png');
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        background-attachment: fixed;
        min-height: 100vh;
      `}
    >
      <div
        css={(theme: Theme) => css`
          position: relative;
          padding: 1em;
          border-bottom: solid 1px #e8e7f2;
          // background: ${theme.lightOcean};
        `}
      >
        <h2
          css={css`
            margin: 0;
            text-align: center;
            @media (max-width: ${breakpoints.xs}) {
              margin-top: 2em;
            }
          `}
        >
          Plans for every business size
        </h2>
        {hasPlan && (
          <Link
            css={css`
              position: absolute;
              top: 1em;
              left: 1em;
              padding: 0;
              margin-right: auto;
              display: flex;
              align-items: center;
              color: inherit;
              padding: 0 1em;
            `}
            to="/admin-plan"
          >
            <div
              css={(theme: Theme) => css`
                display: flex;
                align-items: center;
                color: ${theme.mono70};
              `}
            >
              <BackSVG />
              <span
                css={css`
                  margin-left: 0.25em;
                `}
              >
                Back
              </span>
            </div>
          </Link>
        )}
      </div>
      <div
        css={(theme) => css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 1em;
          // border-bottom: solid 1px #e8e7f2;
          // background: ${theme.lightOcean};
        `}
      >
        <p
          css={(theme) => css`
            margin: 0;
            color: ${theme.mono50};
          `}
        >
          Plans for solo professionals, teams and large organizations.
        </p>

        <span
          css={(theme) => css`
            margin-top: 1.5em;
            text-decoration: underline;
            color: ${theme.deepOcean};
          `}
        >
          <a
            href="https://www.birdi.io/pricing"
            target="_blank"
            rel="noopener noreferrer"
            css={(theme) => css`
              color: ${theme.deepOcean};
            `}
          >
            See full inclusions list
          </a>
        </span>
      </div>
      {!isLoading && (
        <div
          css={css`
            display: flex;
            flex-direction: row;
            padding: 1em 10em;
            @media (max-width: ${breakpoints.xs}) {
              flex-direction: column;
              padding: 2em 0;
            }
          `}
        >
          {plans.length > 0 && (
            <>
              {/* <FreePlanOption
          plan={plans.find(plan => plan.productName?.includes('Free'))}
          displayInterval={paymentInterval}
          currency={meta.organisation?.currency || 'aud'}
        /> */}
              <PlusPlanOption
                product={plans.find((plan) =>
                  plan.productName?.includes('Plus'),
                )}
                displayInterval={paymentInterval}
                currency={meta.organisation?.currency || 'aud'}
              />
              <GrowthPlanOption
                product={plans.find((plan) =>
                  plan.productName?.includes('Growth'),
                )}
                displayInterval={paymentInterval}
                currency={meta.organisation?.currency || 'aud'}
              />
              <UltimateOption displayInterval={paymentInterval} />
            </>
          )}
        </div>
      )}{' '}
      {isLoading && (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 400px;
            padding: 2rem;
          `}
        >
          <LoadingSpinner color="#0078C0" size="50px" />
        </div>
      )}
    </div>
  );
};
